import { Controller } from '@hotwired/stimulus'
import { Chart, registerables } from 'chart.js'
import 'chartjs-adapter-date-fns'

// Register all Chart.js components
Chart.register(...registerables)

/**
 * Accounts Charts Controller
 *
 * Displays account growth, industry distribution, and website status charts
 */
export default class extends Controller {
  static values = {
    growthUrl: String,
    industryUrl: String,
    statusUrl: String
  }

  static targets = [
    'growthChart',
    'industryChart',
    'statusChart',
    'growthLoading',
    'industryLoading',
    'statusLoading'
  ]

  connect() {
    this.growthChart = null
    this.industryChart = null
    this.statusChart = null
    this.loadCharts()
  }

  disconnect() {
    if (this.growthChart) this.growthChart.destroy()
    if (this.industryChart) this.industryChart.destroy()
    if (this.statusChart) this.statusChart.destroy()
  }

  async loadCharts() {
    await Promise.all([this.loadGrowthChart(), this.loadIndustryChart(), this.loadStatusChart()])
  }

  async loadGrowthChart() {
    try {
      const response = await fetch(this.growthUrlValue)
      const data = await response.json()

      if (this.hasGrowthLoadingTarget) {
        this.growthLoadingTarget.style.display = 'none'
      }
      if (this.hasGrowthChartTarget) {
        this.growthChartTarget.style.display = 'block'
      }

      const ctx = this.growthChartTarget.getContext('2d')

      if (this.growthChart) {
        this.growthChart.destroy()
      }

      this.growthChart = new Chart(ctx, {
        type: 'line',
        data: {
          labels: data.data.map((d) => d.date),
          datasets: [
            {
              label: 'New Accounts',
              data: data.data.map((d) => d.count),
              borderColor: 'rgb(59, 130, 246)',
              backgroundColor: 'rgba(59, 130, 246, 0.1)',
              fill: true,
              tension: 0.4,
              pointRadius: 3,
              pointHoverRadius: 5,
              pointBackgroundColor: 'rgb(59, 130, 246)',
              pointBorderColor: 'white',
              pointBorderWidth: 2
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              titleColor: 'white',
              bodyColor: 'white',
              padding: 12,
              displayColors: false,
              callbacks: {
                title: (items) => {
                  const date = new Date(items[0].label)
                  return date.toLocaleDateString('en-US', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric'
                  })
                },
                label: (context) => `${context.parsed.y} new account${context.parsed.y !== 1 ? 's' : ''}`
              }
            }
          },
          scales: {
            y: {
              beginAtZero: true,
              grid: {
                color: 'rgba(0, 0, 0, 0.05)',
                drawBorder: false
              },
              ticks: {
                precision: 0,
                font: { size: 11 },
                color: '#6b7280'
              }
            },
            x: {
              type: 'time',
              time: {
                unit: 'day',
                displayFormats: {
                  day: 'MMM d'
                }
              },
              grid: { display: false },
              ticks: {
                font: { size: 11 },
                color: '#6b7280',
                maxTicksLimit: 10
              }
            }
          },
          interaction: {
            mode: 'nearest',
            axis: 'x',
            intersect: false
          }
        }
      })
    } catch (error) {
      console.error('[AccountsCharts] Error loading growth chart:', error)
      if (this.hasGrowthLoadingTarget) {
        this.growthLoadingTarget.textContent = 'Failed to load chart'
        this.growthLoadingTarget.style.color = 'var(--color-error-600)'
      }
    }
  }

  async loadIndustryChart() {
    try {
      const response = await fetch(this.industryUrlValue)
      const data = await response.json()

      if (this.hasIndustryLoadingTarget) {
        this.industryLoadingTarget.style.display = 'none'
      }
      if (this.hasIndustryChartTarget) {
        this.industryChartTarget.style.display = 'block'
      }

      const ctx = this.industryChartTarget.getContext('2d')

      if (this.industryChart) {
        this.industryChart.destroy()
      }

      this.industryChart = new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels: data.data.map((d) => d.name),
          datasets: [
            {
              data: data.data.map((d) => d.value),
              backgroundColor: data.data.map((d) => d.color),
              borderWidth: 0,
              hoverOffset: 4
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: '60%',
          plugins: {
            legend: {
              position: 'right',
              labels: {
                padding: 12,
                usePointStyle: true,
                pointStyle: 'circle',
                font: { size: 11 },
                color: '#6b7280',
                generateLabels: (chart) => {
                  const datasets = chart.data.datasets
                  return chart.data.labels.map((label, i) => ({
                    text: `${label} (${datasets[0].data[i]})`,
                    fillStyle: datasets[0].backgroundColor[i],
                    strokeStyle: datasets[0].backgroundColor[i],
                    hidden: false,
                    index: i,
                    pointStyle: 'circle'
                  }))
                }
              }
            },
            tooltip: {
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              titleColor: 'white',
              bodyColor: 'white',
              padding: 12,
              callbacks: {
                label: (context) => {
                  const total = context.dataset.data.reduce((a, b) => a + b, 0)
                  const percentage = ((context.parsed / total) * 100).toFixed(1)
                  return `${context.label}: ${context.parsed} (${percentage}%)`
                }
              }
            }
          }
        }
      })
    } catch (error) {
      console.error('[AccountsCharts] Error loading industry chart:', error)
      if (this.hasIndustryLoadingTarget) {
        this.industryLoadingTarget.textContent = 'Failed to load chart'
        this.industryLoadingTarget.style.color = 'var(--color-error-600)'
      }
    }
  }

  async loadStatusChart() {
    try {
      const response = await fetch(this.statusUrlValue)
      const data = await response.json()

      if (this.hasStatusLoadingTarget) {
        this.statusLoadingTarget.style.display = 'none'
      }
      if (this.hasStatusChartTarget) {
        this.statusChartTarget.style.display = 'block'
      }

      const ctx = this.statusChartTarget.getContext('2d')

      if (this.statusChart) {
        this.statusChart.destroy()
      }

      this.statusChart = new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels: data.data.map((d) => d.name),
          datasets: [
            {
              data: data.data.map((d) => d.value),
              backgroundColor: data.data.map((d) => d.color),
              borderWidth: 0,
              hoverOffset: 4
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: '60%',
          plugins: {
            legend: {
              position: 'right',
              labels: {
                padding: 12,
                usePointStyle: true,
                pointStyle: 'circle',
                font: { size: 11 },
                color: '#6b7280',
                generateLabels: (chart) => {
                  const datasets = chart.data.datasets
                  return chart.data.labels.map((label, i) => ({
                    text: `${label} (${datasets[0].data[i]})`,
                    fillStyle: datasets[0].backgroundColor[i],
                    strokeStyle: datasets[0].backgroundColor[i],
                    hidden: false,
                    index: i,
                    pointStyle: 'circle'
                  }))
                }
              }
            },
            tooltip: {
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              titleColor: 'white',
              bodyColor: 'white',
              padding: 12,
              callbacks: {
                label: (context) => {
                  const total = context.dataset.data.reduce((a, b) => a + b, 0)
                  const percentage = ((context.parsed / total) * 100).toFixed(1)
                  return `${context.label}: ${context.parsed} (${percentage}%)`
                }
              }
            }
          }
        }
      })
    } catch (error) {
      console.error('[AccountsCharts] Error loading status chart:', error)
      if (this.hasStatusLoadingTarget) {
        this.statusLoadingTarget.textContent = 'Failed to load chart'
        this.statusLoadingTarget.style.color = 'var(--color-error-600)'
      }
    }
  }
}
